import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FolderUp, Plus, Camera, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";

const CATEGORIES = [
  { value: "TRAINING", label: "Training" },
  { value: "OTHER", label: "Other" },
];

function fmtBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function UploadDialog({ employees, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", category: "TRAINING", title: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setForm({ user_id: "", category: "TRAINING", title: "", notes: "" });
    setFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please choose or capture a file"); return; }
    if (!form.user_id) { toast.error("Please select an employee"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large — max 25 MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("user_id", form.user_id);
    fd.append("category", form.category);
    if (form.title) fd.append("title", form.title);
    if (form.notes) fd.append("notes", form.notes);
    try {
      await api.post("/manager/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      reset(); setOpen(false); onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#1A2744] text-white" data-testid="mgr-docs-upload-btn">
          <Plus className="h-4 w-4 mr-1" /> Upload document
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Upload employee document</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Employee</Label>
            <Select value={form.user_id} onValueChange={(v) => setForm((f) => ({ ...f, user_id: v }))}>
              <SelectTrigger data-testid="mgr-docs-employee"><SelectValue placeholder="Choose employee" /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} <span className="text-xs text-gray-400 ml-1">{e.employee_code}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="mgr-docs-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title (optional)</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Barista Level 2 certificate" data-testid="mgr-docs-title" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="mgr-docs-notes" />
          </div>
          <div className="space-y-2">
            <Label>File</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <label className="flex-1">
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="mgr-docs-file" accept="image/*,application/pdf,.doc,.docx" />
                <span className="cursor-pointer flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  <FileText className="h-4 w-4" /> Choose file
                </span>
              </label>
              <label className="flex-1">
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="mgr-docs-camera" />
                <span className="cursor-pointer flex items-center justify-center gap-2 px-3 py-2 border border-[#1A2744] text-[#1A2744] rounded-lg text-sm hover:bg-[#1A2744]/5">
                  <Camera className="h-4 w-4" /> Take photo
                </span>
              </label>
            </div>
            {file && <div className="text-xs text-gray-600">{file.name} — {fmtBytes(file.size)}</div>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#1A2744] text-white" data-testid="mgr-docs-submit">
              {busy ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ManagerEmployeeDocuments() {
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterUser, setFilterUser] = useState("ALL");

  const load = useCallback(async () => {
    try {
      const [docsR, empR] = await Promise.all([
        api.get("/manager/documents", { params: filterUser !== "ALL" ? { user_id: filterUser } : {} }),
        api.get("/manager/employees-for-docs"),
      ]);
      setDocs(docsR.data.documents || []);
      setEmployees(empR.data.employees || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  }, [filterUser]);

  useEffect(() => { load(); }, [load]);

  const download = (doc) => {
    const url = `${API_BASE}/manager/documents/${doc.id}/download`;
    window.open(url, "_blank");
  };

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <FolderUp className="h-5 w-5 text-[#1A2744]" />
        <h1 className="text-xl font-bold text-[#1A2744]">Upload employee documents</h1>
      </div>
      <p className="text-xs text-gray-600">
        Upload training records or other documents to an employee&apos;s file. Use the camera
        to capture certificates / completion records on the spot.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <UploadDialog employees={employees} onUploaded={load} />
        <Select value={filterUser} onValueChange={setFilterUser}>
          <SelectTrigger className="w-[260px] h-9" data-testid="mgr-docs-filter">
            <SelectValue placeholder="Filter by employee" />
          </SelectTrigger>
          <SelectContent className="max-h-[280px]">
            <SelectItem value="ALL">All employees</SelectItem>
            {employees.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name} <span className="text-xs text-gray-400 ml-1">{e.employee_code}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee</TableHead><TableHead>Title / file</TableHead>
            <TableHead>Category</TableHead><TableHead>Uploaded</TableHead>
            <TableHead>Size</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {docs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6">No documents yet</TableCell></TableRow>}
            {docs.map((d) => (
              <TableRow key={d.id} data-testid={`mgr-doc-row-${d.id}`}>
                <TableCell className="text-sm">{d.user_name} <span className="text-gray-400 font-mono text-[11px] ml-1">{d.employee_code}</span></TableCell>
                <TableCell className="text-sm">
                  <div className="font-medium text-[#1A2744]">{d.title || d.filename}</div>
                  <div className="text-xs text-gray-500 truncate max-w-xs">{d.filename}</div>
                </TableCell>
                <TableCell className="text-xs">{d.category}</TableCell>
                <TableCell className="text-xs text-gray-500">
                  <div>{d.uploaded_at?.slice(0, 16).replace("T", " ")}</div>
                  <div className="text-gray-400">by {d.uploaded_by_name}</div>
                </TableCell>
                <TableCell className="text-xs">{fmtBytes(d.size_bytes)}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => download(d)} data-testid={`mgr-doc-download-${d.id}`}>
                    <Download className="h-3 w-3 mr-1" /> Download
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
